/**
 * Pastilla "Avisos" (cabecera). Badge con nº de no leídos. Panel con dos
 * vistas: "No leídos" (por defecto) y "Leídos", de 15 en 15 con "Ver más".
 * Clic en un aviso -> marca leído y navega al elemento. "Marcar todo leído".
 * Los avisos leídos se autolimpian a los 30 días (índice TTL en backend).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { Bell, CheckCircle, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 15;

function timeAgo(iso, es) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return es ? 'hace un momento' : 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString(es ? 'es-ES' : 'en-GB');
}

export default function NotificationsBell({ lang }) {
  const es = lang === 'es';
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('unread');     // 'unread' | 'read'
  const [hasMore, setHasMore] = useState(false);

  const L = {
    title: es ? 'Avisos' : 'Alerts',
    emptyUnread: es ? 'No tienes avisos sin leer.' : 'No unread alerts.',
    emptyRead: es ? 'No hay avisos leídos.' : 'No read alerts.',
    markAll: es ? 'Marcar todo leído' : 'Mark all read',
    unread: es ? 'No leídos' : 'Unread',
    read: es ? 'Leídos' : 'Read',
    more: es ? 'Ver más' : 'Show more',
  };

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
      setCount(data?.count || 0);
    } catch { /* silencioso */ }
  }, []);

  const fetchList = useCallback(async (status, skip = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/notifications?status=${status}&limit=${PAGE}&skip=${skip}`,
        { withCredentials: true });
      const rows = data?.items || [];
      setItems((prev) => (append ? [...prev, ...rows] : rows));
      setHasMore(!!data?.has_more);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => clearInterval(id);
  }, [fetchCount]);

  useEffect(() => {
    if (open) { setItems([]); fetchList(tab, 0, false); }
  }, [open, tab, fetchList]);

  const handleOpen = async (item) => {
    setOpen(false);
    if (!item.read) {
      try {
        await axios.post(`${API_URL}/api/notifications/${item.id}/read`, {}, { withCredentials: true });
        setCount((c) => Math.max(0, c - 1));
      } catch { /* noop */ }
    }
    if (item.link) navigate(item.link);
  };

  const markAll = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      setCount(0);
      if (tab === 'unread') setItems([]); else fetchList('read', 0, false);
      setHasMore(false);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const switchTab = (t) => { if (t !== tab) { setTab(t); setItems([]); } };

  const dismiss = async (e, item) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    if (!item.read) setCount((c) => Math.max(0, c - 1));
    try {
      await axios.delete(`${API_URL}/api/notifications/${item.id}`, { withCredentials: true });
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="notifications-bell-btn"
          className="h-8 px-2.5 relative flex items-center gap-1.5 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-full"
        >
          <Bell size={16} weight={count > 0 ? 'fill' : 'regular'} className={count > 0 ? 'text-[#1f3e80]' : ''} />
          <span className="text-sm font-body hidden sm:inline">{L.title}</span>
          {count > 0 && (
            <span
              data-testid="notifications-badge"
              className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-100">
          <span className="text-sm font-semibold text-stone-900">{L.title}</span>
          {tab === 'unread' && count > 0 && (
            <button
              type="button"
              onClick={markAll}
              data-testid="notifications-mark-all-btn"
              className="text-xs text-[#1f3e80] hover:underline flex items-center gap-1"
            >
              <CheckCircle size={14} /> {L.markAll}
            </button>
          )}
        </div>

        {/* Pestañas No leídos / Leídos */}
        <div className="flex border-b border-stone-100">
          {['unread', 'read'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => switchTab(t)}
              data-testid={`notifications-tab-${t}`}
              className={`flex-1 text-xs font-medium py-2 transition-colors ${
                tab === t ? 'text-[#1f3e80] border-b-2 border-[#1f3e80]' : 'text-stone-400 hover:text-stone-600'}`}
            >
              {t === 'unread' ? L.unread : L.read}
              {t === 'unread' && count > 0 ? ` (${count > 99 ? '99+' : count})` : ''}
            </button>
          ))}
        </div>

        <div className="max-h-[420px] overflow-y-auto">
          {loading && items.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center">...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center" data-testid="notifications-empty">
              {tab === 'unread' ? L.emptyUnread : L.emptyRead}
            </p>
          ) : (
            <>
              {items.map((it) => (
                <div
                  key={it.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(it)}
                  data-testid={`notification-item-${it.id}`}
                  className={`group relative w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-stone-50 flex gap-2.5 cursor-pointer ${it.read ? '' : 'bg-[#1f3e80]/[0.04]'}`}
                >
                  <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${it.read ? 'bg-transparent' : 'bg-[#1f3e80]'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${it.read ? 'text-stone-700 font-medium' : 'text-stone-900 font-semibold'}`}>{it.title}</span>
                      <span className="text-[11px] text-stone-400 flex-shrink-0 group-hover:opacity-0 transition-opacity">{timeAgo(it.created_at, es)}</span>
                    </span>
                    {it.body && <span className="block text-xs text-stone-500 mt-0.5 line-clamp-2 pr-4">{it.body}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => dismiss(e, it)}
                    data-testid={`notification-dismiss-${it.id}`}
                    title={es ? 'Descartar' : 'Dismiss'}
                    className="absolute top-2.5 right-3 h-6 w-6 rounded-full flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              {hasMore && (
                <button
                  type="button"
                  onClick={() => fetchList(tab, items.length, true)}
                  data-testid="notifications-load-more"
                  className="w-full py-2.5 text-xs font-medium text-[#1f3e80] hover:bg-stone-50"
                >
                  {loading ? '...' : L.more}
                </button>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
